import {Dialpad, Nfc, QrCodeScanner} from "@mui/icons-material";
import {Box, Grid} from "@mui/material";
import {DeviceType} from "@variocube/app-api";
import {
	classNames,
	ImageIcon,
	layout,
	PushButton,
	theme,
	useActivateListener,
	useBoxes,
	useDevicePresent,
	useFlag,
	usePlatform,
	useStorageItemBlob,
	useStorageItemValue,
	View,
	ViewContent,
} from "@variocube/cube-ui";
import * as React from "react";
import {useMemo, useState} from "react";
import {style, stylesheet} from "typestyle";
import {useLocalization} from "../i18n";
import {BookmarkFlow} from "./BookmarkFlow";
import {CodeDialog} from "./CodeDialog";
import deliveryIcon from "./delivery.svg";
import {KeySupport} from "./KeySupport";
import pickupIcon from "./pickup.svg";
import returnIcon from "./return.svg";
import {ReturnDeliveryFlow} from "./ReturnDeliveryFlow";
import {
	Bookmark,
	BookmarkType,
	Delivery,
	deliveryFromBookmark,
	DeliveryState,
	PickupEvent,
	Tenant,
	TenantUser,
} from "./types";

interface HomeViewProps {
	returnDeliveriesEnabled: boolean;
	onAdminLogin: (user: TenantUser) => void;
	onStorage: (delivery: Delivery) => void;
	onPickup: (event: PickupEvent) => void;
	onReturn: () => void;
	onCreateDelivery?: (delivery: Delivery) => void;
}

export function HomeView(props: HomeViewProps) {
	const {
		returnDeliveriesEnabled,
		onAdminLogin,
		onPickup,
		onStorage,
		onReturn,
		onCreateDelivery,
	} = props;
	const {t} = useLocalization();
	const platform = usePlatform();
	const boxes = useBoxes();
	const deliveries = useStorageItemValue<Delivery[]>("deliveries");
	const bookmarks = useStorageItemValue<Bookmark[]>("bookmarks") || [];
	const tenant = useStorageItemValue<Tenant>("tenant");

	const tenantLogoUrl = useStorageItemValue<string>("tenantLogoUrl");
	const backgroundImageBlob = useStorageItemBlob("backgroundImage");
	const backgroundImageUrl = useMemo(() => backgroundImageBlob && URL.createObjectURL(backgroundImageBlob), [
		backgroundImageBlob,
	]);
	const customBackground = backgroundImageUrl && style({backgroundImage: `url("${backgroundImageUrl}")`});

	const [codeOpen, setCodeOpen, clearCodeOpen] = useFlag(false);

	const [returnOpen, setReturnOpen] = useState<boolean>(false);
	const [bookmark, setBookmark] = useState<Bookmark>();
	const [delivery, setDelivery] = useState<Delivery>();

	useActivateListener(clearCodeOpen);

	async function handleCodeVerify(code: string) {
		if (!code) {
			throw new Error("Invalid code");
		}

		const accessBox = boxes.find(box => box.accessKeys?.includes(code));
		if (accessBox) {
			await platform.openBoxLock(accessBox.number);
			return;
		}

		const occupancies = await platform.getOccupancies(code);
		if (occupancies.length > 0) {
			const storageDelivery = deliveries?.find(delivery =>
				delivery.storage.storageCode == code || delivery.storage.storageKey == code
			);
			if (storageDelivery) {
				onStorage(storageDelivery);
				return;
			}
			onPickup({occupancies, pickupRecipient: undefined});
			return;
		}

		const storageDelivery = deliveries?.filter(
			delivery => (delivery.delivery.state == DeliveryState.Created
				|| delivery.delivery.state == DeliveryState.Reserved),
		)
			.find(delivery => delivery.storage.storageCode == code || delivery.storage.storageKey == code);
		if (storageDelivery) {
			onStorage(storageDelivery);
			return;
		}

		const user = tenant?.users.find(user => user.accessCode == code);
		if (user) {
			onAdminLogin(user);
			return;
		}

		throw new Error("Invalid code");
	}

	function handleBookmarkSelect(bookmark: Bookmark) {
		setDelivery(deliveryFromBookmark(bookmark));
		setBookmark(bookmark);
	}

	function handleBookmarkClose() {
		setDelivery(undefined);
		setBookmark(undefined);
	}

	const publicBookmarks = useMemo(() =>
		(bookmarks ?? [])
			.filter(b =>
				(!b.cubeId || b.cubeId === platform.hostname)
				&& b.enableAnonymousDelivery
				&& !b.requireAuthorization
				&& !b.requireConfirmation
				&& !b.requireInternalRecipient
				&& !b.requireRecipientAddress
				&& !b.requireDispatchDetails
			), [bookmarks]);

	function renderBookmarkIcon(type: BookmarkType) {
		switch (type) {
			case BookmarkType.Department:
				return deliveryIcon;
			case BookmarkType.Outbound:
				return pickupIcon;
			default:
				return returnIcon;
		}
	}

	const hasPublicBookmarks = publicBookmarks.length > 0;
	const hasBarcodeReader = useDevicePresent(DeviceType.BarcodeReader);
	const hasNfcReader = useDevicePresent(DeviceType.NfcReader);

	if (codeOpen) {
		return (
			<KeySupport
				onStorage={onStorage}
				onPickup={onPickup}
				onCreateDelivery={onCreateDelivery}
				onAdminLogin={onAdminLogin}
			>
				<CodeDialog
					onClose={clearCodeOpen}
					onVerify={handleCodeVerify}
				/>
			</KeySupport>
		);
	}

	if (tenant?.returnDeliveryConfig && returnOpen) {
		return (
			<ReturnDeliveryFlow
				config={tenant.returnDeliveryConfig}
				onConfirm={() => setReturnOpen(false)}
				onBack={() => setReturnOpen(false)}
			/>
		);
	}

	if (delivery && bookmark) {
		return (
			<BookmarkFlow
				delivery={delivery}
				bookmark={bookmark}
				onDone={handleBookmarkClose}
			/>
		);
	}

	return (
		<KeySupport
			onStorage={onStorage}
			onPickup={onPickup}
			onCreateDelivery={onCreateDelivery}
			onAdminLogin={onAdminLogin}
		>
			<View className={customBackground}>
				{tenantLogoUrl && <img src={tenantLogoUrl} className={styles.tenantLogo} />}
				<ViewContent
					className={classNames(
						layout.fill,
						layout.horizontal,
						layout.center,
						layout.alignCenter,
					)}
				>
					{!hasPublicBookmarks && (
						<>
							<HomeViewItem
								icon={deliveryIcon}
								text={t("Deliver")}
								onClick={setCodeOpen}
							/>
							<HomeViewItem
								icon={pickupIcon}
								text={t("Pickup")}
								onClick={setCodeOpen}
								primary={returnDeliveriesEnabled}
							/>
						</>
					)}
					{publicBookmarks.map(b => (
						<HomeViewItem
							key={b.id}
							icon={renderBookmarkIcon(b.type)}
							text={b.label}
							onClick={() => handleBookmarkSelect(b)}
							alignStart={true}
						/>
					))}
					{returnDeliveriesEnabled && (
						<HomeViewItem
							icon={returnIcon}
							text={t("Return")}
							onClick={onReturn}
							alignStart={hasPublicBookmarks}
						/>
					)}
				</ViewContent>
				{hasPublicBookmarks && (
					<div className={classNames(styles.publicBookmarkFooter, theme.lessContrast)}>
						<Box m={2} textAlign="center" textTransform="uppercase" fontWeight="bold">
							{t("CodeTitle")}
						</Box>
						<Box mx={2} mb={2}>
							<Grid container spacing={2}>
								{hasNfcReader && (
									<Grid item xs>
										<PushButton disabled fullWidth className={styles.button}>
											<Nfc fontSize="large" sx={{m: 2}} />
											<div>{t("NfcButton")}</div>
											<Box fontSize="0.75rem">{t("NfcButtonHint")}</Box>
										</PushButton>
									</Grid>
								)}
								<Grid item xs>
									<PushButton primary fullWidth onClick={setCodeOpen} className={styles.button}>
										<Dialpad fontSize="large" sx={{m: 2}} />
										<div>{t("EnterCodeButton")}</div>
										<Box fontSize="0.75rem">{t("EnterCodeHint")}</Box>
									</PushButton>
								</Grid>
								{hasBarcodeReader && (
									<Grid item xs>
										<PushButton disabled fullWidth className={styles.button}>
											<QrCodeScanner fontSize="large" sx={{m: 2}} />
											<div>{t("BarcodeButton")}</div>
											<Box fontSize="0.75rem">{t("BarcodeButtonHint")}</Box>
										</PushButton>
									</Grid>
								)}
							</Grid>
						</Box>
					</div>
				)}
			</View>
		</KeySupport>
	);
}

interface HomeViewItemProps {
	text: string;
	onClick: () => void;
	icon: string;
	primary?: boolean;
	alignStart?: boolean;
}

function HomeViewItem({text, onClick, icon, primary, alignStart = false}: HomeViewItemProps) {
	const primaryClass = primary ? styles.primaryItem : undefined;
	const alignClass = !alignStart ? styles.itemAlignCenter : undefined;
	return (
		<div onClick={onClick} className={classNames(styles.item, alignClass, primaryClass)}>
			<ImageIcon src={icon} class={styles.itemIcon} />
			<div className={styles.itemText}>{text}</div>
		</div>
	);
}

const styles = stylesheet({
	item: {
		backgroundColor: "var(--color-primary)",
		width: "30vw",
		height: "30vw",
		padding: "2.5em 2em",
		margin: "0 2em",
		boxShadow: "0 1px 10px 0 rgba(0,0,0,0.5)",
		cursor: "pointer",
		$nest: {
			"&:hover": {
				filter: "brightness(1.25)",
			},
		},
	},
	itemAlignCenter: {
		display: 'flex',
		flexDirection: 'column',
		justifyContent: 'center',
	},
	itemIcon: {
		display: "block",
		width: "9rem",
		margin: "0 auto",
	},
	itemText: {
		fontSize: "1.9em",
		fontWeight: 500,
		textAlign: "center",
		color: "#fff",
	},
	tenantLogo: {
		position: "absolute",
		top: "1rem",
		right: "1rem",
		width: "auto",
		height: "8rem",
	},
	primaryItem: {
		zIndex: 1,
		filter: "brightness(105%)",
		transform: "scale(1.1)",
	},
	button: {
		flexFlow: "column",
		padding: "0.5em 0.75em",
	},
	publicBookmarkFooter: {
		flex: "0 0 min-content",
	},
	buttonContainer: {
		flex: 1,
		margin: "1rem",
	},
});
